import React, { Suspense, useEffect, useRef, useState } from "react";
import AppRoutes from "./Routes";
import { fetchUserDetails, mintOauthToken, msalTokenFetcher, redeemSessionToken, fetchRefreshAccessToken, fetchUserAppList, loginUser, issueJwtToken } from "services/modules/login";
import { useLocation, useNavigate } from "react-router-dom";
import { darkModeAtom, msalTokenAtom, oAuthTokenAtom, userDetailsAtom, userPridAtom, currentAppNameAtom, userAppListAtom, currentUserIdAtom, currentAppConfigAtom, currentContentPagesAtom, currentAppQuickPromptsAtom, appSwitchLoaderAtom, isAdminAtom, loginFailureAtom } from "store/atoms/commonAtom";
import { useRecoilState } from "recoil";
import ChatAssistantNoAccess from "./pages/components/Others/chatAssistantNoAccess"

const BUFFER_TIME_MS = 5 * 60 * 1000;
const MAX_MSAL_RETRIES = 2;

const App = () => {
  const [, setMsalToken] = useRecoilState(msalTokenAtom);
  const [, setOAuthToken] = useRecoilState(oAuthTokenAtom);
  const [userDetails, setuserDetails] = useRecoilState(userDetailsAtom);
  const [prid, setuserPrid] = useRecoilState(userPridAtom);
  const [, setDarkmode] = useRecoilState(darkModeAtom);
  const [currentAppName, setCurrentAppName] = useRecoilState(currentAppNameAtom);
  const [, setUserAppList] = useRecoilState(userAppListAtom);
  const [, setUserId] = useRecoilState(currentUserIdAtom);
  const [, setCurrentAppConfig] = useRecoilState(currentAppConfigAtom);
  const [, setCurrentContentPages] = useRecoilState(currentContentPagesAtom);
  const [, setCurrentAppQuickPrompts] = useRecoilState(currentAppQuickPromptsAtom);
  const [, setAppSwitchLoader] = useRecoilState(appSwitchLoaderAtom);
  const [, setIsAdmin] = useRecoilState(isAdminAtom);
  const [, setLoginFailure] = useRecoilState(loginFailureAtom);

  const location = useLocation();
  const navigate = useNavigate();
  const refreshTimeoutRef = useRef(null);
  const initialLoginDoneRef = useRef(false);
  const [showNoAccess, setShowNoAccess] = useState(false);

  useEffect(() => {
    const initializeAuthentication = async () => {
      const sessionToken = new URLSearchParams(location.search).get('sessionToken');

      if (!sessionToken) {
        const currentPath = window.location.pathname;
        if (currentPath && currentPath !== '/') {
          localStorage.setItem('currentRoute', currentPath);
        }
        handleMsalRedirect();
        return;
      }

      // Reset retry count on successful redirect back with sessionToken
      localStorage.removeItem('msalRetryCount');

      try {
        const sessionTokenRes = await redeemSessionToken(sessionToken);
        if (!sessionTokenRes?.data) {
          setLoginFailure({ isOpen: true, message: 'No active session data. Please try again.' });
          return;
        }
        const historyRoute = localStorage.getItem('currentRoute') || '/chat';
        localStorage.removeItem('currentRoute');
        navigate(historyRoute, { replace: true });
        localStorage.setItem('sessionToken', sessionTokenRes.data?.newSessionToken || sessionToken);

        await handleAuthenticatedUser(sessionTokenRes.data);
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoginFailure({
          isOpen: true,
          message: error.message || 'Something went wrong while logging in. Please try again.',
        });
      }
    };

    initializeAuthentication();
  }, []);

  useEffect(() => {
    console.log('app switch')
    if (!currentAppName || !userDetails || !prid) return;
    
    // Skipping the first login as it's handled by handleAuthenticatedUser
    if (!initialLoginDoneRef.current) {
      initialLoginDoneRef.current = true;
      return;
    }
    
    const loginToApp = async () => {
      try {
        await handleUserLogin(userDetails, prid, currentAppName);
        setAppSwitchLoader(false);
      } catch (err) {
        console.error('Error logging in to app:', err);
        setAppSwitchLoader(false); // Reset on error
      }
    };

    loginToApp();
  }, [currentAppName, userDetails, prid]); 

  useEffect(() => {
    const handlePageActivity = async () => {
      if (document.visibilityState === 'visible') {
        if (isTokenExpired()) {
          let success = await refreshAccessToken();
          if (success) {
            scheduleRefreshTimeout();
          }
        }
        else {
          scheduleRefreshTimeout();
        }
      }
      else {
        killRefreshTimeout();
      }
    }

    //start timeout for silent refresh on app mount
    scheduleRefreshTimeout();

    document.addEventListener('visibilitychange', handlePageActivity);
    return () => {
      document.removeEventListener('visibilitychange', handlePageActivity);
      killRefreshTimeout();
    }
  }, [])

  useEffect(() => {
    if (localStorage.getItem("chatDarkMode") !== null) {
      const userDarkMode = localStorage.getItem("chatDarkMode");
      const isDarkMode = userDarkMode === "true";
      setDarkmode(isDarkMode);
    }
    else {
      const colorScheme = window.matchMedia('(prefers-color-scheme: dark)')
      const userlightDarkMode = colorScheme.matches;
      setDarkmode(userlightDarkMode);

      const handleChange = (e) => {
        setDarkmode(e.matches);
      }

      colorScheme.addEventListener('change', handleChange);
      return () => {
        colorScheme.removeEventListener('change', handleChange);
      }
    }
  }, [])

  const handleAuthenticatedUser = async (sessionData) => {
    setMsalToken(sessionData);
    localStorage.setItem('accessExpiry', sessionData?.expiresOn);

    // Issue a server-signed JWT API token (validates MSAL token via Graph internally)
    const tokenRes = await issueJwtToken(sessionData?.accessToken).catch(() => { throw new Error('Unable to issue JWT API token as request failed. Please try again.'); });
    if (tokenRes?.data?.token) {
      localStorage.setItem('apiToken', tokenRes.data.token);
    } else {
      throw new Error('Unable to issue JWT API token. Please try again.');
    }

    const userPrid = sessionData?.account?.username.split('@')[0];
    setuserPrid(userPrid);

    // Fetch User Details
    const userDetailsResponse = await fetchUserDetails(sessionData?.accessToken).catch(() => { throw new Error('Unable to fetch user details. Please try again.'); });
    setuserDetails(userDetailsResponse?.data);

    // Get User App access list
    const userAppListJSON = await fetchUserAppList(userPrid).catch(() => { throw new Error('Unable to fetch app access list. Please try again.'); });
    const userAppAccessList = userAppListJSON?.data?.userAppAccess || [];
    setUserAppList(userAppAccessList);
    const checkAdminAccess = userAppListJSON?.data?.totalAppsWithAdminAccessCount > 0;
    setIsAdmin(checkAdminAccess);

    // Initialize default app
    const { appName: initializedAppName, theme, accessDenied, adminDenied } = initializeDefaultApp(userAppAccessList, checkAdminAccess, navigate);
    if (accessDenied) {
      setShowNoAccess(true);
      return;
    }
    if (adminDenied) {
      navigate("/noaccess", { replace: true });
      return null;
    }

    // Login + config
    await handleUserLogin(userDetailsResponse?.data, userPrid, initializedAppName);
    setAppSwitchLoader(false);

    // Mint OAuth token
    const oauthRes = await mintOauthToken(sessionData.accessToken, sessionData.idToken, initializedAppName).catch(() => { throw new Error('Unable to authenticate. Please try again.'); });

    if (!oauthRes?.data?.access_token) {
      throw new Error('Unable to authenticate. Please try again.');
    }
    setOAuthToken(oauthRes.data.access_token);
  };

  const scheduleRefreshTimeout = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    let tokenExpiry = localStorage.getItem('accessExpiry');
    if (!tokenExpiry) return;

    let currentTime = Date.now();
    let formattedTokenExpiry = new Date(tokenExpiry).getTime();
    let timeUntilRefresh = formattedTokenExpiry - currentTime - BUFFER_TIME_MS;
    console.log('Scheduling token refresh in ms:', timeUntilRefresh, 'min:', timeUntilRefresh / 60000);

    if (timeUntilRefresh <= 0) {
      refreshAccessToken().then(success => {
        if (success) {
          scheduleRefreshTimeout();
        }
      });
    }
    else {
      refreshTimeoutRef.current = setTimeout(async () => {
        const success = await refreshAccessToken();
        if (success) {
          scheduleRefreshTimeout();
        }
      }, timeUntilRefresh);
    }
  };

  const killRefreshTimeout = () => {
    //To kill the timeout in event loop when app becomes idle in background
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }

  const isTokenExpired = () => {
    const tokenExpiry = localStorage.getItem('accessExpiry');
    if (!tokenExpiry) return true;

    const expiryTime = new Date(tokenExpiry).getTime();
    const currentTime = Date.now();
    return currentTime + BUFFER_TIME_MS >= expiryTime;
  };

  const refreshAccessToken = async () => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) {
      console.error('No session token found for refresh');
      handleMsalRedirect();
      return;
    }
    try {
      const silentRefreshTokenResponse = await fetchRefreshAccessToken(sessionToken);
      if (silentRefreshTokenResponse?.data) {
        localStorage.setItem('accessExpiry', silentRefreshTokenResponse?.data?.expiresOn);
        setMsalToken(silentRefreshTokenResponse?.data);

        // Refresh the server-issued JWT API token
        try {
          const tokenRes = await issueJwtToken(silentRefreshTokenResponse?.data?.accessToken);
          if (tokenRes?.data?.token) {
            localStorage.setItem('apiToken', tokenRes.data.token);
          }
        } catch (err) {
          console.error('Failed to refresh API token:', err);
          setLoginFailure({ isOpen: true, message: 'Unable to authenticate. Please try again.' });
        }

        try {
          const oauthRes = await mintOauthToken(silentRefreshTokenResponse?.data?.accessToken, silentRefreshTokenResponse?.data?.idToken, currentAppName);
          if (oauthRes?.data?.access_token) {
            setOAuthToken(oauthRes.data.access_token);
          } else {
            console.error('mintOauthToken: No data in response');
            setLoginFailure({ isOpen: true, message: 'Unable to authenticate. Please try again.' });
          }
        }
        catch (error) {
          console.error('mintOauthToken failed:', error);
          setLoginFailure({ isOpen: true, message: 'Unable to authenticate. Please try again.' });
        }
      }
      else {
        handleMsalRedirect();
      }
    }
    catch (err) {
      console.error('Error refreshing access token:', err);
      handleMsalRedirect();
    }
  }

  const initializeDefaultApp = (userAppAccessList, checkAdminAccess, navigate) => {
    if (!userAppAccessList.some(app => app.hasAccess)) {
      console.error('User does not have access rights to any chat assistant apps.');
      return { appName: null, theme: null, accessDenied: true };
    }
  
    if(!checkAdminAccess && window.location.pathname === '/admin'){
      navigate("/noaccess", { replace: true });
      return { appName: null, theme: null, adminDenied: true };
    }
    // Check if there's a saved app from session recovery
    const savedAppName = localStorage.getItem('currentAppName');
    let targetApp;
    if (savedAppName) {
      targetApp = userAppAccessList.find(app => app.hasAccess && app.appName === savedAppName);
    }
    // Fallback to first accessible app if saved app not found
    if (!targetApp) {
      targetApp = userAppAccessList.find(app => app.hasAccess);
    }
    setCurrentAppName(targetApp.appName);
    localStorage.setItem('currentAppName', targetApp.appName);
    // Return both appName and theme from API response
    return {
      appName: targetApp.appName,
      theme: targetApp.appMetaData?.theme || 'az', // Use theme from API, fallback to 'az'
      accessDenied: false,
      adminDenied: false,
    };
  };

  const handleUserLogin = async (userDetailsData, prid, initializedAppName) => {
    try {
      const userLoginResponse = await loginUser(userDetailsData, prid, initializedAppName);

      if (!userLoginResponse?.data || !userLoginResponse?.data?.userId) {
        console.error('Login failed: No userId returned');
        setLoginFailure({ isOpen: true, message: 'Login failed. No valid user session was returned. Please try again.' });
        return false;
      }

      const userIdFromLoginResponse = userLoginResponse?.data?.userId;
      const userAppConfigResponse = userLoginResponse?.data?.appConfig;
      const userContentPagesResponse = userLoginResponse?.data?.contentPages;
      const userQuickPromptsResponse = userAppConfigResponse?.quickPrompts;

      setUserId(userIdFromLoginResponse);
      setCurrentAppConfig(userAppConfigResponse);
      setCurrentContentPages(userContentPagesResponse);
      setCurrentAppQuickPrompts(userQuickPromptsResponse || []);

      // --- Content Pages ID change detection (per-app) ---
      // Build a map of contentType -> contentPagesId from the login response
      const newContentIds = {};
      if (Array.isArray(userContentPagesResponse)) {
        userContentPagesResponse.forEach((page) => {
          if (page.contentType && page.contentPagesId) {
            newContentIds[page.contentType] = page.contentPagesId;
          }
        });
      }

      // Retrieve the per-app stored IDs from localStorage
      const storedIdsRaw = localStorage.getItem('contentPagesIds');
      let allStoredIds = storedIdsRaw ? JSON.parse(storedIdsRaw) : {};

      // Migrate legacy flat format: remove top-level content-type keys (e.g. "about", "announcement")
      // that were stored by the old code, keeping only app-name keys (whose values are objects)
      const legacyKeys = Object.keys(allStoredIds).filter(
        (key) => typeof allStoredIds[key] === 'string'
      );
      if (legacyKeys.length > 0) {
        legacyKeys.forEach((key) => delete allStoredIds[key]);
      }

      // Get stored IDs for the current app
      const storedAppIds = allStoredIds[initializedAppName] || null;

      // Detect which content types have changed for this app
      const changedContentTypes = {};
      if (!storedAppIds) {
        // First visit to this app — flag about for display
        changedContentTypes['about'] = true;
      } else {
        Object.keys(newContentIds).forEach((key) => {
          if (storedAppIds[key] !== newContentIds[key]) {
            changedContentTypes[key] = true;
          }
        });
      }

      if (Object.keys(changedContentTypes).length > 0) {
        localStorage.setItem('showContentUpdateFlag', JSON.stringify(changedContentTypes));
      }

      // Persist the latest content page IDs under this app's key
      allStoredIds[initializedAppName] = newContentIds;
      localStorage.setItem('contentPagesIds', JSON.stringify(allStoredIds));

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setLoginFailure({ isOpen: true, message: 'Something went wrong while logging in. Please try again.' });
      return false;
    }
  };

  // Wrapper to prevent infinite MSAL redirect loops
  const handleMsalRedirect = () => {
    const retryCount = Number.parseInt(localStorage.getItem('msalRetryCount') || '0', 10);
    if (retryCount >= MAX_MSAL_RETRIES) {
      console.error('Max MSAL retry attempts reached');
      localStorage.removeItem('msalRetryCount');
      setLoginFailure({ isOpen: true, message: 'Unable to connect. Please try again later.' });
      return;
    }
    localStorage.setItem('msalRetryCount', String(retryCount + 1));
    localStorage.removeItem('accessExpiry');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('apiToken');
    msalTokenFetcher();
  };

  if (showNoAccess) {
    return <ChatAssistantNoAccess />;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AppRoutes />
    </Suspense>
  );
}

export default App;